import { EventEmitter } from 'eventemitter3';
import { LocalTrackEvent, IRoom, ITrack, LocalMixVideoTrack, LocalScreenTrack, LocalVideoTrack, MediaType, RemoteVideoTrack, SMALL_MODE, TIMER_TYPE, VideoCodecPipelineType, VideoPlayerMode } from '..';
import { ELECTRON_MAJOR_VERSION, IOS_MAIN_VERSION, IS_ANY_SAFARI, IS_SAFARI_15_1 } from '../utils/environment';
import loggerManager from '../utils/log/logger-manager';
import { Logger } from '../index';
import timer from '../utils/timer';
import { isRotate90Or270, loadImage } from '../utils/utils';
import { Video2dContext, VideoGlContext } from './video-context';
import { SmallVideoTrackDestination, VideoDestinationNode, VideoTrackDestination, VideoTrackDestination4allin1 } from './video-node/destination';
import { VideoImageSourceNode, VideoTrackSourceNode } from './video-node/source';
import { VideoTransformNode } from './video-node/transform';
import { VideoMixNode } from './video-node/mix';
import { VideoNode } from './video-node/base';
import { VBNode } from './video-node/vb';
import RtcError from '../common/rtc-error';
import ErrorCode from '../common/error-code';
import kvStatManager, { KV_REPORT_KEY_VIDEO_MANAGER } from './kv-stat-manager';
import { Track } from '../track/track';
import { SEI_MESSAGE } from '../utils/codec-pipeline/sei-utils';
import { NALU } from '../utils/nalu';
import { DecodeContext, DecodeContextOption } from './video-decoder';
import { catchError, createDecorator, ProcessHandler } from '../utils/decorators/middleware';
import type { mat4 } from 'gl-matrix';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT from '../inner-emitter/inner-event';

export interface VideoEncodeProcessorParam { frame: RTCEncodedVideoFrame, mediaType?: MediaType, seiMessageList?: SEI_MESSAGE[], onDump?: () => void; }
export interface VideoDecodeProcessorParam { frame: RTCEncodedVideoFrame, track?: RemoteVideoTrack, onSEI?: (naluList: NALU[]) => void, onDump?: () => void; }
export type VideoEncodeProcessor = (param: VideoEncodeProcessorParam) => RTCEncodedVideoFrame;
export type VideoDecodeProcessor = (param: VideoDecodeProcessorParam) => RTCEncodedVideoFrame | undefined;
export interface PostProcessing { init: (gl: WebGL2RenderingContext, positionBuffer: WebGLBuffer, texCoordBuffer: WebGLBuffer, ratio: number) => void; close: () => void; postProcessing: (mask: WebGLTexture) => WebGLTexture, ratio: number; }

type Matrix4 = typeof mat4;

export interface Resolution {
  width: number;
  height: number;
}

export interface VirtualBackgroundOption {
  type: 'blur' | 'color' | 'image';
  imageUrl?: string;
  blurLevel?: number;
  assetPath?: string;
  enableFaceCentering?: boolean;
  enableEffectOptimization?: boolean;
  onAbort?: (err: any) => void;
  color?: [number, number, number];
}
const updatedPromise = Promise.resolve();
export class VideoManager extends EventEmitter<{ error: [Error]; 'output-track-changed': []; }> {
  videoContext?: Video2dContext | VideoGlContext;
  private _glVideoContext: VideoGlContext;
  private _2dVideoContext: Video2dContext;
  destination?: VideoTrackDestination<Video2dContext | VideoGlContext>;
  smallVideoContext: Video2dContext;
  smallDestination?: SmallVideoTrackDestination;
  smallTrackSource?: VideoTrackSourceNode;
  smallImageSource?: VideoImageSourceNode;
  private _isMirror = false;
  private _rotation: 0 | 90 | 180 | 270 = 0;
  cameraTrack?: LocalVideoTrack;
  cameraNode?: VideoImageSourceNode | VideoTrackSourceNode<Video2dContext> | VBNode;
  transformNode?: VideoTransformNode;
  mixNode?: VideoMixNode;
  screenTrack?: LocalScreenTrack;
  screenNode?: VideoTrackSourceNode;
  selfModel = false;
  blurRadius = 3;
  arTrack?: MediaStreamVideoTrack;
  _enableFaceCentering = false;
  _enableEffectOptimization = false;
  onAbort?: (err: any) => void;
  private _color: [number, number, number] = [0.0, 1.0, 0.0];

  Wasm?: any;

  waterMarkNode?: VideoImageSourceNode;
  private _waterMarkOption?: { x: number, y: number, width: number, height: number, image: HTMLImageElement; };
  private watermarkImageList: Array<{ x: number, y: number, width: number, height: number, image: HTMLImageElement; imageUrl: string, type: 'watermark' | 'mute' | 'custom', zIndex: number; fillVideo?: boolean; }> = [];
  private _beautyParams?: { beauty: number, brightness: number, ruddy: number; };
  isUsingArTrack = false;

  mixTrack?: LocalMixVideoTrack;
  private _isMixScreen = false;
  private _virtualBackground?: HTMLImageElement | 'blur' | 'color';
  private _virtualBackgroundAbortCallback?: (err: any) => void;
  virtualBackgroundInstance: any;
  private _bgAssetPath?: string;
  private log: Logger;
  private _mat4: Matrix4;
  private _postProcessing: PostProcessing;
  private _checkId = 0;
  private _use2d = false;
  private _autoSwitchRenderMode = true;

  encodePipeline: (VideoEncodeProcessor | undefined)[] = [];
  decodePipeline: (VideoDecodeProcessor | undefined)[] = [];
  updated = updatedPromise;
  private _updateFlag = false; // 用于合并多次 update 操作
  constructor(public room?: IRoom) {
    super();
    this.log = loggerManager.createLogger({
      parent: room?.getLogger(),
      id: 'vm',
      userId: room?.userId,
      sdkAppId: room?.sdkAppId
    });
    this.smallVideoContext = new Video2dContext({ frameRate: 15, logger: this.log, name: 's' });
    this.enablePrintDetail();
  }

  get smallMode() {
    return this.room?.smallMode || SMALL_MODE.CANVAS;
  }
  get _hasVirtualBg() {
    return !!this._virtualBackground;
  }
  get _hasWaterMark() {
    return this.watermarkImageList.length > 0;
  }
  get _isRotate() {
    return this._rotation !== 0;
  }
  get _isTransform() {
    return this._isMirror || this._isRotate;
  }
  get renderMode() {
    return this._autoSwitchRenderMode ? 'auto' : this._use2d ? '2d' : 'webgl';
  }
  set renderMode(mode: '2d' | 'webgl' | 'auto') {
    this._autoSwitchRenderMode = mode === 'auto';
    if (this._autoSwitchRenderMode) return;
    const use2d = mode === '2d';
    if (this._use2d === use2d) return;
    this._use2d = use2d;
    this.clear();
    this.videoContext = this._use2d ? this.get2dVideoContext() : this.getGlVideoContext();
    this.update();
  }
  get cameraResolution() {
    const { width, height } = this.cameraTrack?.settings || {};
    return isRotate90Or270(this._rotation) ? { width: height, height: width } : { width, height };
  }
  get2dVideoContext() {
    if (this._2dVideoContext) {
      this._2dVideoContext.destroy();
    } else {
      this._2dVideoContext = new Video2dContext({ frameRate: 15, logger: this.log, name: 'm' });
    }
    this._2dVideoContext.create({ alpha: this._hasWaterMark || this._hasVirtualBg });
    return this._2dVideoContext;
  }
  getGlVideoContext() {
    if (!this._glVideoContext) {
      this._glVideoContext = new VideoGlContext({ frameRate: 15, logger: this.log, name: 'm' });
    } else if (this._glVideoContext.available) {
      return this._glVideoContext;
    }
    this.initializeGlVideoContext();
    return this._glVideoContext;
  }
  initializeGlVideoContext() {
    try {
      this._glVideoContext.create(ELECTRON_MAJOR_VERSION <= 22);
      this._glVideoContext.on(VideoGlContext.UNAVAILABLE, (reason: RtcError): void => {
        this.emit('error', reason);
        this.log.warn('video context unavailable', reason);
        this._virtualBackgroundAbortCallback?.(reason);
        this.update().catch(error => {
          this.log.error(error);
        });
      });
    } catch (error: any) {
      this.emit('error', error);
    }
  }
  initVirtualBackground(onAbort: (err: any) => void, mat4: Matrix4, postProcessing: PostProcessing) {
    this.onAbort = onAbort;
    this._mat4 = mat4;
    this._postProcessing = postProcessing;
  }
  enablePrintDetail(delay = 2000) {
    this._checkId = timer.run(TIMER_TYPE.INTERVAL, () => {
      if (this.destination) {
        this.log.debug(this.destination.getInfo());
      }
    }, { delay });
  }
  destroy() {
    this._2dVideoContext?.destroy();
    this._glVideoContext?.destroy();
    this.smallVideoContext.destroy();
    timer.clearTask(this._checkId);
  }
  get needAlpha() {
    return this._hasWaterMark || this._hasVirtualBg;
  }
  get active() {
    const needActive = (IS_ANY_SAFARI || this._isMixScreen || this._isTransform || this._hasWaterMark || this._hasVirtualBg || this._beautyParams);
    return needActive && this.checkOrCreateVideoContext();
  }
  sendCreateResult(apiName: 'videoCtxGl' | 'videoCtx2d' = 'videoCtxGl', error?: Error) {
    const key = apiName === 'videoCtxGl' ? KV_REPORT_KEY_VIDEO_MANAGER.START_VIDEO_CONTEXT_GL : KV_REPORT_KEY_VIDEO_MANAGER.START_VIDEO_CONTEXT_2D;
    if (error) {
      kvStatManager.addFailedEvent({ key, error });
    } else {
      kvStatManager.addSuccessEvent({ key });
    }
  }
  checkOrCreateVideoContext() {
    const used2d = this._use2d;
    if (this._autoSwitchRenderMode) this._use2d = !this._hasVirtualBg;
    if (this.videoContext) {
      if (this.videoContext.available) {
        const alphaChanged = !this.videoContext.hasAlpha && this.needAlpha;
        const modeChanged = this._autoSwitchRenderMode && used2d === this._hasVirtualBg;
        if (modeChanged) {
          this.clear();
        } else if (alphaChanged) {
          if (this._use2d) this.clear();
          else return true;
        } else {
          return true;
        }
      } else {
        // 尝试重连webgl
        this._glVideoContext = new VideoGlContext({ frameRate: 15, logger: this.log, name: 'm' });
        this.initializeGlVideoContext();
        if (this._glVideoContext.available) {
          this.videoContext = this._glVideoContext;
          return this.videoContext.available;
        }
        this.log.warn('webgl is still not available');
        this.clear();
        this._use2d = true;
      }
    }
    this.videoContext = this._use2d ? this.get2dVideoContext() : this.getGlVideoContext();
    return this.videoContext.available;
  }
  get smallTrack() {
    return this.smallDestination?.videoTrack;
  }
  get hasSmall() {
    return !!this.smallTrack;
  }
  get initialTrack() {
    return this.cameraTrack?.mediaTrack;
  }
  setSmallVideo(small?: VideoProfile, mainOutCanvas?: HTMLCanvasElement) {
    if (this.smallMode === SMALL_MODE.API) return;
    if (small) {
      if (!this.smallVideoContext.available) {
        this.smallVideoContext.create({ alpha: false });
        if (!this.smallVideoContext.available) {
          return;
        }
        this.smallDestination = new SmallVideoTrackDestination(this.smallVideoContext, small, this.log);
        this.smallVideoContext.on(VideoGlContext.UNAVAILABLE, (reason: RtcError): void => {
          this.log.warn('small video context lost', reason);
        });
      }
      this.smallVideoContext.frameRate = small.frameRate;
      this.smallDestination!.resolution = small;
      if (mainOutCanvas) {
        if (this.smallTrackSource) {
          this.smallTrackSource.close();
          delete this.smallTrackSource;
        }
        if (this.smallImageSource) this.smallImageSource.image = mainOutCanvas;
        else {
          this.smallImageSource = this.smallVideoContext.createVideoImageSource(mainOutCanvas);
          this.smallImageSource.resize(mainOutCanvas.width, mainOutCanvas.height);
          this.smallImageSource.connect(this.smallDestination!);
        }
      } else {
        if (this.smallImageSource) {
          this.smallImageSource.close();
          delete this.smallImageSource;
        }
        if (this.smallTrackSource) {
          this.smallTrackSource.replaceTrack(this.initialTrack!);
        } else {
          this.smallTrackSource = this.smallVideoContext.createVideoTrackSource(this.initialTrack!, 'smallTrackSource');
          const { width, height } = this.cameraTrack!.settings;
          this.smallTrackSource.resize(width, height);
          this.smallTrackSource.connect(this.smallDestination!);
        }
      }
    } else if (this.smallVideoContext.available) {
      this.smallVideoContext.destroy();
      delete this.smallDestination;
      delete this.smallTrackSource;
      delete this.smallImageSource;
    }
  }
  _setMainOutput(mainOutCanvas?: HTMLCanvasElement) {
    try {
      const mainTrack = this.cameraTrack!;
      const { small, player } = mainTrack;
      if (IS_ANY_SAFARI) player.setCanvas(mainOutCanvas);
      let outputTrack = (mainOutCanvas && this.destination?.videoTrack) || this.initialTrack!;
      if (this.isUsingArTrack && this.arTrack) {
        this.emit('output-track-changed');
        outputTrack = this.arTrack;
      }
      this.log.info(`set main output ${outputTrack ? outputTrack.label : 'no output track'}`);
      this.setSmallVideo(small, mainOutCanvas);
      // TIP: 抛出前处理后的 video Track , 合图插件使用内部的 video track时，要替换前处理后的 video Track
      // TIP: 多实例，innerEmitter 事件会广播到全部实例，合图插件需要通过 room 实例来判断是哪个实例的 video Track
      innerEmitter.emit(INNER_EVENT.LOCAL_VIDEO_TRACK_PREPROCESSED, { mediaTrack: outputTrack, profile: this.cameraTrack?.profile, room: this.room });
      return mainTrack.setOutputMediaStreamTrack(outputTrack);
    } catch (err) {
      this.log.error('set main output failed', err);
    }
  }
  @catchError(function (this: VideoManager, error: any) {
    this.log.error('update failed', error);
  })
  @createDecorator((next: ProcessHandler<VideoManager>) => async function (this: VideoManager, ...args: any[]) {
    if (this._updateFlag) return;
    this._updateFlag = true;
    await updatedPromise; // 收集同步调用的多个 update 操作
    this.updated = new Promise((resolve, reject) => {
      next.apply(this, args).then(resolve, reject);
      setTimeout(reject, 5000, new RtcError({ code: ErrorCode.API_CALL_TIMEOUT, message: 'update timeout' }));
    });
    this._updateFlag = false;
    await this.updated;
  })
  async update(changeCameraOnly = false) {
    if (!this.cameraTrack || !this.initialTrack) return;
    if (!this.active) {
      if (this.cameraNode) this.clear();
      return this._setMainOutput();
    }
    const { settings, profile } = this.cameraTrack;
    if (this._use2d || (!this._virtualBackground && !this._beautyParams)) {
      if (!this.destination) {
        this.destination = this.videoContext!.createVideoTrackDestination({ name: 'mainDestination2d', logger: this.log });
        this.destination.on(VideoNode.RENDER, (source: CanvasImageSource) => {
          this.cameraTrack?.emit(LocalTrackEvent.RENDER, source);
        });
      }
      if (IOS_MAIN_VERSION === 16) {
        if (this.initialTrack instanceof CanvasCaptureMediaStreamTrack) {
          if (this.cameraNode) {
            if (this.cameraNode instanceof VideoTrackSourceNode) {
              this.cameraNode.close();
              delete this.cameraNode;
            } else this.cameraNode.image = this.initialTrack.canvas;
          }
          if (!this.cameraNode) this.cameraNode = (this.videoContext as Video2dContext).createVideoImageSource(this.initialTrack.canvas, { name: 'cameraCanvasSource', logger: this.log });
        } else {
          if (this.cameraNode) {
            if (this.cameraNode instanceof VideoTrackSourceNode) this.cameraNode.replaceTrack(this.initialTrack!);
            else {
              this.cameraNode.close();
              delete this.cameraNode;
            }
          }
          if (!this.cameraNode) this.cameraNode = (this.videoContext as Video2dContext).createVideoTrackSource(this.initialTrack!, 'cameraTrackSource');
        }
      } else {
        if (this.cameraNode) (this.cameraNode as VideoTrackSourceNode).replaceTrack(this.initialTrack);
        else this.cameraNode = this.videoContext!.createVideoTrackSource(this.initialTrack, 'cameraNodeSource');
      }
      this.cameraNode!.resize(settings.width, settings.height);
    } else {
      if (changeCameraOnly && this.cameraNode && this.destination) {
        (this.cameraNode as VBNode).replaceTrack(this.initialTrack);
      } else {
        if (this.cameraNode) this.cameraNode.close();
        if (!this.destination) {
          this.destination = new VideoTrackDestination4allin1(this.videoContext! as VideoGlContext, { name: 'mainDestination', logger: this.log });
          this.destination.on(VideoNode.RENDER, (source: CanvasImageSource) => {
            this.cameraTrack?.emit(LocalTrackEvent.RENDER, source);
          });
        } else this.destination.disableCheckMute();
        const { width, height } = this.cameraResolution;
        const image = await this.getWatermarkImage(width, height);
        this._waterMarkOption = { x: 0, y: 0, width: image.width, height: image.height, image };
        this.cameraNode = new VBNode(this.videoContext as VideoGlContext, {
          input: this.initialTrack,
          width,
          height,
          mirror: this._isMirror,
          rotation: this._rotation,
          bg: this._virtualBackground,
          selfModel: this.selfModel,
          waterMark: this._waterMarkOption,
          beautyParams: this._beautyParams,
          useTflite: true,
          blurRadius: this.blurRadius,
          assetPath: this._bgAssetPath,
          Wasm: this.Wasm,
          enableFaceCentering: this._enableFaceCentering,
          enableEffectOptimization: this._enableEffectOptimization,
          onAbort: this.onAbort,
          mat4: this._mat4,
          postProcessing: this._postProcessing,
          color: this._color
        });
        this.cameraNode.connect(this.destination as VideoTrackDestination4allin1);
        this.destination.enableCheckMute();
        await this.cameraNode.predictReady;
      }
    }
    this.videoContext!.frameRate = profile.frameRate;

    if (this._use2d) {
      let lastNode = this.cameraNode as VideoNode<TexImageSource>;
      lastNode.disconnect();
      if (this._isTransform) {
        if (!this.transformNode) {
          this.transformNode = new VideoTransformNode(this.videoContext as Video2dContext, this.log, this._isMirror, this._rotation);
        } else {
          this.transformNode.mirror = this._isMirror;
          this.transformNode.rotation = this._rotation;
        }
        lastNode = lastNode.connect(this.transformNode);
        lastNode.disconnect();
        this.log.info(`start mirror ${this._isMirror} rotate ${this.rotation}`);
      }
      if (this.mixNode) this.mixNode.close();
      delete this.mixNode;
      const needMix = this._isMixScreen || this._hasWaterMark;
      if (needMix) {
        this.mixNode = new VideoMixNode(this.videoContext as Video2dContext, this.log);
        lastNode.connect(this.mixNode, { zIndex: 1 });
        if (this._hasWaterMark && !this.waterMarkNode && this._waterMarkOption) {
          this.waterMarkNode = (this.videoContext as Video2dContext).createVideoImageSource(this._waterMarkOption.image, { autoResize: false, logger: this.log });
          this.waterMarkNode.resize(this._waterMarkOption.width, this._waterMarkOption.height);
          this.waterMarkNode.x = this._waterMarkOption.x;
          this.waterMarkNode.y = this._waterMarkOption.y;
        } else if (this.waterMarkNode) {
          const { width, height } = this.cameraResolution;
          this.waterMarkNode.image = await this.getWatermarkImage(width, height);
          if (width && height) this.waterMarkNode.resize(width, height);
        }
        this.waterMarkNode?.connect(this.mixNode, { zIndex: 2 });
        if (this._isMixScreen && this.screenTrack) {
          if (!this.screenNode) {
            this.screenNode = (this.videoContext as Video2dContext).createVideoTrackSource(this.screenTrack.mediaTrack as MediaStreamVideoTrack, 'screenNodeSource');
            // screenNode 在第一帧画面时不是 screenTrack 的宽高，手动设置
            this.screenNode.resize(this.screenTrack.settings.width, this.screenTrack.settings.height);
          }
          this.screenNode.shouldUpdate = false;
          this.screenNode.connect(this.mixNode, { zIndex: 0 });
        }
        lastNode = this.mixNode;
        this.log.info('start mix', `${this.mixNode.width}x${this.mixNode.height}`);
      }
      lastNode.connect(this.destination);
    }
    this.log.info(`update ${this._use2d ? '2d' : 'webgl'}`);
    return this._setMainOutput(this.videoContext!.canvas);
  }
  clearLastFrame() { // mixNode 没有输入节点时 destination 画面会卡在最后一帧画面，需手动清空
    if (this.destination) this.destination.ctx2d?.clearRect(0, 0, this.destination.width, this.destination.height);
  }
  changeInput(track: Track<'video'>) {
    if (track instanceof LocalScreenTrack) {
      this.log.info('change screen input', track.mediaTrack?.label);
      return this.setScreenTrack(track);
    }
    if (track instanceof LocalVideoTrack) {
      this.log.info('change video input', track.mediaTrack?.label);
      return this.setCameraTrack(track);
    }
    if (track instanceof RemoteVideoTrack) {
      this.log.info('change remote input', track.mediaTrack?.label);
      const outputTrack = track.mediaTrack!;
      return track.setOutputMediaStreamTrack(outputTrack);
    }
    if (track instanceof LocalMixVideoTrack) {
      this.log.info('change mix input', track.outMediaTrack?.label);
      return this.setMixTrack(track);
    }
    this.log.warn('change unknown input', track.mediaTrack?.label);
  }
  removeInput(track: ITrack) {
    if (track instanceof LocalScreenTrack) {
      this.screenNode?.close();
      delete this.screenNode;
      delete this.screenTrack;
      this.update();
    } else if (track instanceof LocalVideoTrack) {
      if (this._isMixScreen) {
        delete this.cameraNode;
        this.cameraTrack!._inputTrack = null;
        this.update();
      } else {
        this.clear();
        delete this.cameraTrack;
        if (this.smallImageSource) {
          this.smallImageSource.close();
          delete this.smallImageSource;
        }
        if (this.smallTrackSource) {
          this.smallTrackSource.close();
          delete this.smallTrackSource;
        }
      }
    } else if (track instanceof RemoteVideoTrack) {
      // do nothing
      if (track.source) {
        track.source.context.destroy();
      }
    } else if (track instanceof LocalMixVideoTrack) {
      delete this.mixTrack;
      this.update();
    }
  }
  setMixTrack(track: LocalMixVideoTrack) {
    this.mixTrack = track;
    // return this.update();
  }
  setCameraTrack(track: LocalVideoTrack) {
    this.cameraTrack = track;
    return this.update(true);
  }
  async setScreenTrack(track: LocalScreenTrack) {
    this.screenTrack = track;
    if (this._isMixScreen) {
      if (this.screenNode) {
        this.screenNode.replaceTrack(track.mediaTrack as MediaStreamVideoTrack);
      } else {
        await this.update();
      }
    }
    return track.setOutputMediaStreamTrack(track.mediaTrack!);
  }
  async getWatermarkImage(canvasWidth?: number, canvasHeight?: number): Promise<HTMLImageElement> {
    const canvas = document.createElement('canvas');
    if (canvasHeight && canvasWidth) {
      canvas.height = canvasHeight;
      canvas.width = canvasWidth;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new RtcError({
        code: ErrorCode.NOT_SUPPORTED,
        message: 'Make image failed because of canvas context is null'
      });
    }
    this.watermarkImageList.sort((a, b) => a.zIndex - b.zIndex);
    this.watermarkImageList.forEach(({ image, x, y, width, height, fillVideo }) => {
      // 如果是全屏水印，使用画布尺寸
      const drawWidth = fillVideo ? (canvasWidth || width) : width;
      const drawHeight = fillVideo ? (canvasHeight || height) : height;
      const drawX = fillVideo ? 0 : x;
      const drawY = fillVideo ? 0 : y;
      ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    });
    return loadImage(canvas.toDataURL());
  }
  pushWaterMarkImageList(newImage: { x: number, y: number, width: number, height: number, image: HTMLImageElement; imageUrl: string, type: 'watermark' | 'mute' | 'custom', zIndex: number; fillVideo?: boolean; }) {
    const { type } = newImage;
    const exists = this.watermarkImageList.some(image => image.imageUrl === newImage.imageUrl &&
      image.height === newImage.height &&
      image.width === newImage.width &&
      image.x === newImage.x &&
      image.y === newImage.y &&
      image.type === newImage.type &&
      image.zIndex === newImage.zIndex &&
      image.fillVideo === newImage.fillVideo);
    if (exists) return;
    if (type === 'mute' || type === 'watermark') {
      this.watermarkImageList = this.watermarkImageList.filter(item => item.type !== type);
    }
    this.watermarkImageList.push(newImage);
  }
  async setBeautyParams(params: { beauty: number, brightness: number, ruddy: number; }) {
    this._beautyParams = params;
    this.update();
  }
  async stopBeauty() {
    this._beautyParams = void 0;
    this.update();
  }
  async setWatermark(option: { x: number, y: number, width?: number, height?: number, imageUrl: string; imageElement?: HTMLImageElement; type?: 'mute' | 'watermark' | 'custom', zIndex?: number; fillVideo?: boolean; }) {
    let image;
    try {
      image = await loadImage(option?.imageElement || option.imageUrl);
    } catch (e) {
      throw new RtcError({
        code: ErrorCode.INVALID_PARAMETER,
        message: `load image failed, url: ${option.imageUrl}`
      });
    }
    const { x = 0, y = 0, width = image.width, height = image.height, type = 'watermark', zIndex = 2, fillVideo = false } = option;
    const isHaveWaterMark = this.watermarkImageList.some(item => item.type === type);
    if (isHaveWaterMark) {
      this.watermarkImageList = this.watermarkImageList.filter(item => item.type !== type);
      this.pushWaterMarkImageList({ x, y, width, height, image, zIndex, type, imageUrl: option.imageUrl, fillVideo });
      image = await this.getWatermarkImage(this.cameraResolution.width, this.cameraResolution.height);
      this._waterMarkOption = { x: 0, y: 0, width: image.width, height: image.height, image };
      if (!this.waterMarkNode) {
        // webgl 渲染时没有 watermarkNode，直接执行 update()
        this.update();
      } else {
        this.waterMarkNode.x = 0;
        this.waterMarkNode.y = 0;
        this.waterMarkNode.resize(image.width, image.height);
        this.waterMarkNode.image = image;
      }
    } else {
      this.pushWaterMarkImageList({ x, y, width, height, image, zIndex, type, imageUrl: option.imageUrl, fillVideo });
      await this.freshWatermark();
    }
    this.log.info('set watermark', JSON.stringify(this.watermarkImageList, (key, value) => (key === 'imageUrl' ? undefined : value)));
  }
  async deleteWatermark(type: 'mute' | 'watermark' | 'custom' = 'watermark') {
    this.watermarkImageList = this.watermarkImageList.filter(item => item.type !== type);
    this.log.info('delete watermark', type, JSON.stringify(this.watermarkImageList, (key, value) => (key === 'imageUrl' ? undefined : value)));
    await this.freshWatermark();
  }
  async freshWatermark() {
    this.waterMarkNode?.close();
    delete this.waterMarkNode;
    delete this._waterMarkOption;
    const { width, height } = this.cameraResolution;
    const image = await this.getWatermarkImage(width, height);
    this._waterMarkOption = { x: 0, y: 0, width: image.width, height: image.height, image };
    this.update();
  }
  async setVirtualBackground(option?: VirtualBackgroundOption) {
    if (!option) {
      delete this._virtualBackground;
      delete this._virtualBackgroundAbortCallback;
    } else {
      if (option.onAbort) this._virtualBackgroundAbortCallback = option.onAbort;
      if (this._use2d && !this._autoSwitchRenderMode) return Promise.reject(new Error('not support virtual background in 2d mode'));
      this._bgAssetPath = option.assetPath;
      if (option.type === 'image') {
        this._virtualBackground = await loadImage(option.imageUrl!);
      } else {
        this.blurRadius = option.blurLevel || this.blurRadius || 3;
        this._virtualBackground = option.type;
      }
      this._enableFaceCentering = option.enableFaceCentering ?? this._enableFaceCentering;
      this._enableEffectOptimization = option.enableEffectOptimization ?? this._enableEffectOptimization;
      this._color = option.color ?? this._color;
    }
    this.log.info(`${this._virtualBackground ? 'start' : 'stop'} virtual background, ${option?.type || ''}, ${this.blurRadius || ''}`);
    // 如果没有摄像头轨道，先存储配置，等摄像头打开后 setCameraTrack -> update 会自动生效
    if (!this.cameraTrack?.mediaTrack) {
      this.log.info('no camera track, virtual background config saved and will take effect when camera is available');
      return;
    }
    await this.update();
    if (this._virtualBackground && !this._glVideoContext.available) {
      throw new RtcError({
        code: ErrorCode.INVALID_OPERATION,
        message: `webgl context create failed, ${this._glVideoContext.error}`
      });
    }
  }
  public get mixScreen() {
    return this._isMixScreen;
  }
  public set mixScreen(value) {
    this._isMixScreen = value;
    if (!this._isMixScreen) {
      this.screenNode?.close();
      delete this.screenNode;
    }
    this.update();
  }
  set mirror(mirror: boolean) {
    if (this._isMirror === mirror) return;
    this._isMirror = mirror;
    if (!this._isTransform) {
      this.transformNode?.close();
      delete this.transformNode;
    }
    this.update();
  }
  get mirror() {
    return this._isMirror;
  }
  set rotation(angle: 0 | 90 | 180 | 270) {
    if (this._rotation === angle) return;
    this._rotation = angle;
    if (!this._isTransform) {
      this.transformNode?.close();
      delete this.transformNode;
    }
    this.update();
  }
  get rotation() {
    return this._rotation;
  }
  enableAr(track: MediaStreamVideoTrack) {
    this.arTrack = track;
    this.isUsingArTrack = true;
    this.update();
  }
  async updateAr() {
    if (!this.cameraTrack?.mediaTrack) return;
    await this.virtualBackgroundInstance.ar.updateInputTrack(this.cameraTrack.mediaTrack.clone());
  }
  disableAr() {
    this.isUsingArTrack = false;
    this.arTrack?.stop();
    this.arTrack = undefined;
    this.update();
  }

  createDecodeContext(option: DecodeContextOption) {
    return new DecodeContext(option);
  }

  clear() {
    this.videoContext?.disconnect();
    this.destination?.removeAllListeners();
    delete this.destination;
    delete this.cameraNode;
    delete this.transformNode;
    delete this.screenNode;
    delete this.waterMarkNode;
  }

  addEncodeProcessor({ processor, type }: { processor: VideoEncodeProcessor, type: VideoCodecPipelineType; }) {
    if (this.encodePipeline.includes(processor)) return;
    this.encodePipeline[type] = processor;
    this.room?.enableInsertableStreams();
  }
  addDecodeProcessor({ processor, type }: { processor: VideoDecodeProcessor, type: VideoCodecPipelineType; }) {
    if (this.decodePipeline.includes(processor)) return;
    this.decodePipeline[type] = processor;
    this.room?.enableInsertableStreams();
  }
  removeEncodeProcessor({ type }: { type: VideoCodecPipelineType; }) {
    this.encodePipeline[type] = void 0;
  }
  removeDecodeProcessor({ type }: { type: VideoCodecPipelineType; }) {
    this.decodePipeline[type] = void 0;
  }
}
